(function () {
  // ─────────────────────────────────────────────────────────────
  //  DUAL-MODE TRAVEL PRICE CALCULATOR
  //
  //  pricing_mode: "per_night"  → variantPrice is per-person-per-night
  //                               (Bahia Principe, Lopesan, Royalton simple, etc.)
  //                               Formula: price × rooms × nights  +  addons
  //
  //  pricing_mode: "per_stay"   → variantPrice is total per room for the full stay
  //                               (Grand Palladium, Hard Rock, Royalton Riviera, etc.)
  //                               Formula: price × rooms  +  addons
  //                               Nights selector is informational only.
  //
  //  Set in Shopify admin via metafield: custom.pricing_mode = "per_night" | "per_stay"
  //  Pass into liquid template as: "pricing_mode": {{ product.metafields.custom.pricing_mode | json }}
  // ─────────────────────────────────────────────────────────────

  function moneyFormat(cents, moneyFormatStr) {
    try { return Shopify.formatMoney(cents, moneyFormatStr); }
    catch (e) { return "$" + (cents / 100).toFixed(2); }
  }

  function clampInt(el, min) {
    const v = parseInt(el.value || String(min), 10);
    const fixed = isNaN(v) ? min : Math.max(min, v);
    el.value = fixed;
    return fixed;
  }

  function init(root) {
    const dataEl = root.querySelector('[data-travel-json]');
    if (!dataEl) return;

    let data = {};
    try { data = JSON.parse(dataEl.textContent || "{}"); } catch (e) { return; }

    // ── Pricing mode ─────────────────────────────────────────────
    // "per_night" = price is per person per night (multiply by rooms × nights)
    // "per_stay"  = price is total for the full stay (multiply by rooms only)
    const pricingMode = (data.pricing_mode || "per_stay").toLowerCase();
    const isPerNight  = pricingMode === "per_night";

    // ── Add-on rates (in cents) ───────────────────────────────────
    const AIR_ADULT = +data.AIR_ADULT || 0;
    const AIR_CHILD = +data.AIR_CHILD || 0;
    const TR_ADULT  = +data.TR_ADULT  || 0;
    const TR_CHILD  = +data.TR_CHILD  || 0;
    const HOTEL_CHD = +data.HOTEL_CHD || 0;

    const variants = Array.isArray(data.variants) ? data.variants : [];
    const moneyFmt = data.money_format || "${{amount}}";

    // ── Input elements ────────────────────────────────────────────
    const roomsEl    = root.querySelector("#tp_rooms");
    const checkinEl  = root.querySelector("#tp_checkin");
    const checkoutEl = root.querySelector("#tp_checkout");
    const nightsEl   = root.querySelector("#tp_nights");   // hidden
    const adultsEl   = root.querySelector("#tp_adults");
    const kidsEl     = root.querySelector("#tp_children");

    const airfareEl   = root.querySelector("#tp_airfare");
    const transfersEl = root.querySelector("#tp_transfers");
    const hotelChdEl  = root.querySelector("#tp_hotel_child");

    // ── Output elements ───────────────────────────────────────────
    const roomSubEl      = root.querySelector("#tp_room_sub");
    const airSubEl       = root.querySelector("#tp_air_sub");
    const trSubEl        = root.querySelector("#tp_tr_sub");
    const chdSubEl       = root.querySelector("#tp_chd_sub");
    const totalEl        = root.querySelector("#tp_total");
    const roomLabelEl    = root.querySelector("#tp_room_label");
    const nightsRowEl    = root.querySelector("#tp_nights_row");   // optional: wrap nights UI in this el

    // ── Hidden order properties ───────────────────────────────────
    const totalProp     = root.querySelector("#tp_total_prop");
    const breakdownProp = root.querySelector("#tp_breakdown_prop");
    const addonsProp    = root.querySelector("#tp_addons_prop");
    const totalCentsEl  = root.querySelector("#tp_total_cents");

    // ── Traveler fields container ─────────────────────────────────
    const travelerWrap = root.querySelector("#traveler-details");

    // ── Variant ID input (inside same product form) ───────────────
    const variantIdInput =
      root.querySelector('input[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] input[name="id"]');

    // Required elements check
    if (!roomsEl || !adultsEl || !kidsEl ||
        !airfareEl || !transfersEl || !hotelChdEl ||
        !roomSubEl || !airSubEl || !trSubEl || !chdSubEl || !totalEl ||
        !totalProp || !breakdownProp || !addonsProp ||
        !travelerWrap || !variantIdInput) {
      console.warn("[travel-calc] Missing required elements — calculator not initialized.");
      return;
    }

    // ── Hide nights UI for per_stay products ──────────────────────
    // If your template wraps the nights date pickers in #tp_nights_row,
    // this will hide them automatically for per_stay products.
    if (!isPerNight && nightsRowEl) {
      nightsRowEl.style.display = "none";
    }

    // ── Helpers ───────────────────────────────────────────────────
    function getCurrentVariant() {
      const id = parseInt(variantIdInput.value, 10);
      return variants.find(v => v.id === id) || null;
    }

    // Auto checkout date (+7 days from checkin)
    let checkoutWasAuto = false;

    function addDaysISO(isoDate, days) {
      const d = new Date(isoDate + "T00:00:00");
      d.setDate(d.getDate() + days);
      const y  = d.getFullYear();
      const m  = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }

    function maybeAutoSetCheckout() {
      if (!checkinEl || !checkoutEl) return;
      if (!checkinEl.value) return;
      checkoutEl.min = checkinEl.value;
      const suggested = addDaysISO(checkinEl.value, 7);
      if (!checkoutEl.value || checkoutWasAuto) {
        checkoutEl.value = suggested;
        checkoutWasAuto = true;
      }
    }

    function markCheckoutManual() { checkoutWasAuto = false; }

    function calcNights() {
      if (!checkinEl || !checkoutEl) return 1;
      if (!checkinEl.value || !checkoutEl.value) return 1;
      const [y1, m1, d1] = checkinEl.value.split("-").map(Number);
      const [y2, m2, d2] = checkoutEl.value.split("-").map(Number);
      const n = Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000);
      return Number.isFinite(n) ? Math.max(1, n) : 1;
    }

    // ── Traveler fields ───────────────────────────────────────────
    function renderTravelerFields(total) {
      travelerWrap.innerHTML = "";

      const hiddenTotal = document.createElement("input");
      hiddenTotal.type  = "hidden";
      hiddenTotal.name  = "properties[Travelers]";
      hiddenTotal.value = String(total);
      travelerWrap.appendChild(hiddenTotal);

      for (let i = 1; i <= total; i++) {
        const box = document.createElement("div");
        box.style.cssText = "background:#f5f5f5;padding:15px;border-radius:8px;margin-bottom:12px;";

        const h = document.createElement("h4");
        h.textContent = "Traveler " + i + " Details";
        h.style.margin = "0 0 10px";
        box.appendChild(h);

        const mkField = (label, type, name) => {
          const l = document.createElement("label");
          l.textContent = label;
          l.style.cssText = "display:block;margin:10px 0 4px;";
          box.appendChild(l);

          const inp = document.createElement("input");
          inp.type        = type;
          inp.name        = name;
          inp.autocomplete = "off";
          inp.style.cssText = "width:100%;padding:10px;border:1px solid #ccc;border-radius:6px;box-sizing:border-box;";
          box.appendChild(inp);
        };

        mkField("Full Name:",        "text", `properties[Traveler ${i} Name]`);
        mkField("Date of Birth:",    "date", `properties[Traveler ${i} Birthdate]`);
        mkField("Passport Number:",  "text", `properties[Traveler ${i} Passport Number]`);

        travelerWrap.appendChild(box);
      }
    }

    // ── Main update function ──────────────────────────────────────
    function update() {
      const v = getCurrentVariant();
      if (!v) return;

      const rooms  = clampInt(roomsEl, 1);
      const adults = clampInt(adultsEl, 1);
      const kids   = Math.max(0, parseInt(kidsEl.value || "0", 10));
      kidsEl.value = kids;

      const nights = calcNights();
      if (nightsEl) nightsEl.value = String(nights);

      // ── Room label ──────────────────────────────────────────────
      if (roomLabelEl) {
        roomLabelEl.textContent = isPerNight
          ? `(${rooms} room(s) × ${nights} night(s) × price/person/night)`
          : `(${rooms} room(s) × package price — ${nights} night(s))`;
      }

      // ── Child supplement UX ─────────────────────────────────────
      if (kids === 0) {
        hotelChdEl.checked  = false;
        hotelChdEl.disabled = true;
      } else {
        hotelChdEl.disabled = false;
        if (!hotelChdEl.checked) hotelChdEl.checked = true;
      }

      // ── Render traveler fields ──────────────────────────────────
      renderTravelerFields(adults + kids);

      // ── Room subtotal ───────────────────────────────────────────
      //  per_night:  price × rooms × nights
      //  per_stay:   price × rooms   (nights already baked into variant price)
      const roomSub = isPerNight
        ? v.price * rooms * nights
        : v.price * rooms;

      // ── Add-on subtotals ────────────────────────────────────────
      const airSub = airfareEl.checked   ? (AIR_ADULT * adults) + (AIR_CHILD * kids) : 0;
      const trSub  = transfersEl.checked ? (TR_ADULT  * adults) + (TR_CHILD  * kids) : 0;
      const chdSub = hotelChdEl.checked  ? (HOTEL_CHD * kids)                        : 0;

      const total = roomSub + airSub + trSub + chdSub;

      // ── Update display ──────────────────────────────────────────
      roomSubEl.textContent = moneyFormat(roomSub, moneyFmt);
      airSubEl.textContent  = moneyFormat(airSub,  moneyFmt);
      trSubEl.textContent   = moneyFormat(trSub,   moneyFmt);
      chdSubEl.textContent  = moneyFormat(chdSub,  moneyFmt);
      totalEl.textContent   = moneyFormat(total,   moneyFmt);

      // ── Store exact total in cents ──────────────────────────────
      if (totalCentsEl) totalCentsEl.value = String(total);

      // ── Shopify quantity → drives checkout line-item total ──────
      //  per_night:  qty = rooms × nights  (price per night × qty = room total)
      //  per_stay:   qty = rooms            (price per stay  × qty = room total)
      const qtyInput = document.getElementById("Quantity-" + (data.section_id || ""));
      if (qtyInput) {
        qtyInput.value = isPerNight ? rooms * nights : rooms;
      }

      // ── Order properties ────────────────────────────────────────
      totalProp.value = moneyFormat(total, moneyFmt);

      const addons = [
        airfareEl.checked ? "Airfare" : null,
        transfersEl.checked ? "Transfers" : null,
        (hotelChdEl.checked && HOTEL_CHD > 0 && kids > 0) ? "Child hotel supplement" : null
      ].filter(Boolean).join(", ") || "None";

      addonsProp.value = addons;

      const roomBreakdown = isPerNight
        ? `Rooms: ${rooms} × Nights: ${nights} × ${moneyFormat(v.price, moneyFmt)}/person/night = ${moneyFormat(roomSub, moneyFmt)}`
        : `Rooms: ${rooms} × ${moneyFormat(v.price, moneyFmt)}/room (full stay, ${nights} nights) = ${moneyFormat(roomSub, moneyFmt)}`;

      breakdownProp.value = [
        roomBreakdown,
        `Adults: ${adults}, Children: ${kids}`,
        `Airfare: ${moneyFormat(airSub, moneyFmt)}`,
        `Transfers: ${moneyFormat(trSub, moneyFmt)}`,
        `Child hotel: ${moneyFormat(chdSub, moneyFmt)}`,
        `Pricing mode: ${pricingMode}`,
        `Estimated total: ${moneyFormat(total, moneyFmt)}`
      ].join(" | ");
    }

    // ── Event listeners ───────────────────────────────────────────
    [roomsEl, adultsEl, kidsEl, airfareEl, transfersEl, hotelChdEl].forEach(el => {
      el.addEventListener("input",  update);
      el.addEventListener("change", update);
    });

    if (checkinEl) {
      checkinEl.addEventListener("input",  function () { maybeAutoSetCheckout(); update(); });
      checkinEl.addEventListener("change", function () { maybeAutoSetCheckout(); update(); });
    }
    if (checkoutEl) {
      checkoutEl.addEventListener("input",  function () { markCheckoutManual(); update(); });
      checkoutEl.addEventListener("change", function () { markCheckoutManual(); update(); });
    }

    // Shopify variant selectors
    document.addEventListener("change", function (e) {
      if (e.target && (e.target.name || "").includes("options")) update();
      if (e.target && e.target.closest("variant-radios"))         update();
      if (e.target && e.target.closest("variant-selects"))        update();
    });

    // ── Init ──────────────────────────────────────────────────────
    maybeAutoSetCheckout();
    update();
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-travel-calc]").forEach(init);
  });
})();
